/**
 * Assessment Form Page
 * 
 * Hybrid assessment form with:
 * - Sectioned layout with progress tracking
 * - Auto-save on every field change
 * - AI intelligent follow-up questions
 * - Real-time completion percentage
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AppShell from "../../components/layout/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  User,
  Heart,
  Home,
  Brain,
  Activity,
  Shield,
  Calendar,
  Check,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Save,
  Sparkles,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { UniversalIntakePanel } from "../../components/ui/universal-intake-form";

const API = process.env.REACT_APP_BACKEND_URL;

// Section configuration
const SECTIONS = [
  { key: "demographics", label: "Demographics", icon: User },
  { key: "emergency_contacts", label: "Emergency Contacts", icon: Phone },
  { key: "medical", label: "Medical History", icon: Heart },
  { key: "adl", label: "ADLs", icon: Activity },
  { key: "iadl", label: "IADLs", icon: Home },
  { key: "cognitive", label: "Cognitive/Behavioral", icon: Brain },
  { key: "safety", label: "Safety & Mobility", icon: Shield },
  { key: "scheduling", label: "Scheduling", icon: Calendar },
];

// Default empty assessment structure
const EMPTY_ASSESSMENT = {
  demographics: {
    first_name: "",
    last_name: "",
    preferred_name: "",
    date_of_birth: "",
    gender: "",
    primary_phone: "",
    email: "",
    address_line1: "",
    city: "",
    state: "",
    zip_code: "",
    primary_language: "English",
    interpreter_needed: false,
    funding_source: "",
  },
  emergency_contacts: [],
  diagnoses: [],
  medications: [],
  allergies: [],
  adl: {
    bathing_independent: true,
    bathing_assistance_level: null,
    bathing_assistance_type: null,
    dressing_independent: true,
    dressing_assistance_level: null,
    grooming_independent: true,
    grooming_tasks: [],
    toileting_independent: true,
    toileting_assistance_level: null,
    toileting_incontinence: false,
    toileting_incontinence_type: null,
    transferring_independent: true,
    transferring_assistance_level: null,
    eating_independent: true,
    eating_assistance_level: null,
    eating_diet_restrictions: [],
  },
  iadl: {
    meal_prep_independent: true,
    meal_prep_frequency: null,
    housekeeping_independent: true,
    housekeeping_tasks_needed: [],
    laundry_independent: true,
    shopping_independent: true,
    transportation_independent: true,
    transportation_drives: false,
    medication_reminder_needed: false,
    medication_setup_needed: false,
  },
  cognitive: {
    orientation_person: true,
    orientation_place: true,
    orientation_time: true,
    memory_short_term: null,
    memory_long_term: null,
    dementia_diagnosis: false,
    dementia_type: null,
  },
  behavioral: {
    wandering_risk: false,
    exit_seeking: false,
    verbal_aggression: false,
    physical_aggression: false,
    resistance_to_care: false,
    anxiety: false,
    depression: false,
  },
  home_safety: {
    home_type: null,
    steps_to_enter: 0,
    handrails_present: false,
    bathroom_safety_equipment: [],
    smoke_detectors: true,
    pets_in_home: false,
    weapons_in_home: false,
  },
  mobility: {
    ambulation_status: null,
    ambulation_device: null,
    transfers_independent: true,
    balance_standing: null,
  },
  fall_risk: {
    history_of_falls: false,
    falls_last_6_months: 0,
    fall_risk_level: null,
  },
  scheduling: {
    recommended_hours_per_week: null,
    preferred_days: [],
    preferred_times: null,
    caregiver_gender_preference: null,
    caregiver_language_requirement: null,
  },
};

export default function AssessmentForm() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("demographics");
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Debounce timer ref
  const saveTimerRef = useRef(null);

  // Fetch assessment
  const fetchAssessment = async () => {
    try {
      const response = await axios.get(`${API}/api/assessments/${assessmentId}`, {
        withCredentials: true,
      });
      setAssessment({
        ...EMPTY_ASSESSMENT,
        ...response.data,
        demographics: { ...EMPTY_ASSESSMENT.demographics, ...response.data.demographics },
        adl: { ...EMPTY_ASSESSMENT.adl, ...response.data.adl },
        iadl: { ...EMPTY_ASSESSMENT.iadl, ...response.data.iadl },
        cognitive: { ...EMPTY_ASSESSMENT.cognitive, ...response.data.cognitive },
        behavioral: { ...EMPTY_ASSESSMENT.behavioral, ...response.data.behavioral },
        home_safety: { ...EMPTY_ASSESSMENT.home_safety, ...response.data.home_safety },
        mobility: { ...EMPTY_ASSESSMENT.mobility, ...response.data.mobility },
        fall_risk: { ...EMPTY_ASSESSMENT.fall_risk, ...response.data.fall_risk },
        scheduling: { ...EMPTY_ASSESSMENT.scheduling, ...response.data.scheduling },
      });
    } catch (err) {
      console.error("Failed to fetch assessment:", err);
      toast.error("Failed to load assessment");
      navigate("/assessments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessment();
  }, [assessmentId]);

  // Auto-save section
  const autoSave = useCallback(async (section, data) => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/api/assessments/${assessmentId}/auto-save`,
        { section, data },
        { withCredentials: true }
      );
      setLastSaved(new Date());
      setAssessment(prev => ({
        ...prev,
        completion_percentage: response.data.completion_percentage,
        status: response.data.status,
      }));
    } catch (err) {
      console.error("Auto-save failed:", err);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  }, [assessmentId]);

  // Handle field change with debounced auto-save
  const handleFieldChange = (section, field, value) => {
    // Update local state immediately
    setAssessment(prev => {
      const updated = { ...prev };
      if (section === "demographics" || section === "adl" || section === "iadl" || 
          section === "cognitive" || section === "behavioral" || section === "home_safety" ||
          section === "mobility" || section === "fall_risk" || section === "scheduling") {
        updated[section] = { ...updated[section], [field]: value };
      } else {
        updated[section] = value;
      }
      
      // Debounced save
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = setTimeout(() => {
        autoSave(section, updated[section]);
      }, 500);
      
      return updated;
    });

    // Check for follow-up questions on certain fields
    checkFollowUpQuestions(section, field, value);
  };

  // Check for AI follow-up questions
  const checkFollowUpQuestions = async (section, field, value) => {
    // Only check for significant fields
    const triggerFields = [
      "dementia_diagnosis", "diabetes", "bathing_independent", 
      "history_of_falls", "wandering_risk", "toileting_incontinence"
    ];
    
    if (!triggerFields.includes(field)) return;
    
    try {
      const response = await axios.post(
        `${API}/api/assessments/${assessmentId}/follow-up-questions`,
        { section, field, value },
        { withCredentials: true }
      );
      if (response.data.questions?.length > 0) {
        setFollowUpQuestions(response.data.questions);
      }
    } catch (err) {
      console.error("Failed to get follow-up questions:", err);
    }
  };

  // Mark assessment complete
  const handleMarkComplete = async () => {
    try {
      await axios.post(
        `${API}/api/assessments/${assessmentId}/complete`,
        {},
        { withCredentials: true }
      );
      toast.success("Assessment marked complete");
      navigate(`/assessments/${assessmentId}/care-plan`);
    } catch (err) {
      console.error("Failed to mark complete:", err);
      toast.error(err.response?.data?.detail || "Failed to mark complete");
    }
    setShowCompleteDialog(false);
  };

  // Navigate sections
  const goToNextSection = () => {
    const currentIndex = SECTIONS.findIndex(s => s.key === activeSection);
    if (currentIndex < SECTIONS.length - 1) {
      setActiveSection(SECTIONS[currentIndex + 1].key);
    }
  };

  const goToPrevSection = () => {
    const currentIndex = SECTIONS.findIndex(s => s.key === activeSection);
    if (currentIndex > 0) {
      setActiveSection(SECTIONS[currentIndex - 1].key);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AppShell>
    );
  }

  if (!assessment) {
    return (
      <AppShell>
        <div className="text-center py-12">
          <p className="text-gray-500">Assessment not found</p>
          <Button onClick={() => navigate("/assessments")} className="mt-4">
            Back to Assessments
          </Button>
        </div>
      </AppShell>
    );
  }

  const clientName = assessment.demographics?.first_name && assessment.demographics?.last_name
    ? `${assessment.demographics.first_name} ${assessment.demographics.last_name}`
    : "New Client Assessment";

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/assessments")}
              className="mb-2"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Assessments
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">{clientName}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge variant="outline">
                {assessment.status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
              </Badge>
              <span className="text-sm text-gray-500">
                {assessment.completion_percentage}% complete
              </span>
              {saving && (
                <span className="text-sm text-gray-400 flex items-center">
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  Saving...
                </span>
              )}
              {lastSaved && !saving && (
                <span className="text-sm text-gray-400">
                  <Check className="w-3 h-3 inline mr-1" />
                  Saved
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {assessment.status === "assessment_in_progress" && (
              <Button
                onClick={() => setShowCompleteDialog(true)}
                className="bg-primary hover:bg-primary/90"
                data-testid="mark-complete-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Mark Complete
              </Button>
            )}
            {(assessment.status === "assessment_complete" || assessment.status === "ai_draft_care_plan") && (
              <Button
                onClick={() => navigate(`/assessments/${assessmentId}/care-plan`)}
                className="bg-primary hover:bg-primary/90"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                View Care Plan
              </Button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Assessment Progress</span>
              <span className="text-sm text-gray-500">{assessment.completion_percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${assessment.completion_percentage}%` }}
              ></div>
            </div>
          </CardContent>
        </Card>

        {/* Universal Intake Panel — AI-Assisted Data Entry */}
        <UniversalIntakePanel
          entityType="assessment"
          existingData={assessment}
          onDataExtracted={(extractedData) => {
            // Helper to update nested field
            const updateNestedField = (section, field, value) => {
              setAssessment(prev => ({
                ...prev,
                [section]: {
                  ...prev[section],
                  [field]: value,
                },
              }));
            };
            
            // Map extracted assessment fields to the form
            Object.entries(extractedData).forEach(([key, value]) => {
              if (value === null || value === undefined) return;
              
              // Handle client_name split
              if (key === 'client_name' && typeof value === 'string') {
                const parts = value.split(' ');
                if (parts.length >= 2) {
                  updateNestedField('demographics', 'first_name', parts[0]);
                  updateNestedField('demographics', 'last_name', parts.slice(1).join(' '));
                }
                return;
              }
              
              // Demographics mappings
              const demoFields = ['first_name', 'last_name', 'date_of_birth', 'email', 'address', 'city', 'state', 'zip_code'];
              if (demoFields.includes(key)) {
                updateNestedField('demographics', key, value);
                return;
              }
              if (key === 'phone') {
                updateNestedField('demographics', 'primary_phone', value);
                return;
              }
              
              // ADL fields
              const adlFields = ['bathing_status', 'bathing_notes', 'dressing_status', 'grooming_status', 'toileting_status', 'transferring_status', 'eating_status'];
              if (adlFields.includes(key)) {
                updateNestedField('adl', key, value);
                return;
              }
              
              // IADL fields
              const iadlFields = ['meal_prep_status', 'housekeeping_status', 'laundry_status', 'medication_management_status'];
              if (iadlFields.includes(key)) {
                updateNestedField('iadl', key, value);
                return;
              }
              
              // Cognitive fields
              const cognitiveFields = ['orientation', 'memory', 'dementia_diagnosis', 'dementia_type'];
              if (cognitiveFields.includes(key)) {
                updateNestedField('cognitive', key, value);
                return;
              }
              
              // Safety fields
              const safetyFields = ['fall_risk', 'fall_history', 'wandering_risk', 'recommended_services'];
              if (safetyFields.includes(key)) {
                updateNestedField('safety', key, value);
                return;
              }
              
              // Scheduling
              if (key === 'recommended_hours') {
                updateNestedField('scheduling', 'recommended_hours', value);
                return;
              }
            });
            
            toast.success('Assessment data populated from AI extraction');
          }}
        />

        {/* Follow-up Questions Alert */}
        {followUpQuestions.length > 0 && (
          <Card className="border-purple-200 bg-purple-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-purple-700">
                <Sparkles className="w-4 h-4" />
                AI Follow-up Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {followUpQuestions.map((q, i) => (
                <div key={i} className="mb-3 last:mb-0">
                  <p className="text-sm font-medium text-purple-900">{q.question}</p>
                  <p className="text-xs text-purple-600 mt-1">{q.why_asking}</p>
                  {q.options && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {q.options.map((opt, j) => (
                        <Button
                          key={j}
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={() => {
                            // Handle option selection
                            setFollowUpQuestions(prev => prev.filter((_, idx) => idx !== i));
                          }}
                        >
                          {opt}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 text-purple-600"
                onClick={() => setFollowUpQuestions([])}
              >
                Dismiss
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Section Tabs */}
        <Tabs value={activeSection} onValueChange={setActiveSection}>
          <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-gray-100 p-1">
            {SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <TabsTrigger
                  key={section.key}
                  value={section.key}
                  className="flex-1 min-w-[120px] data-[state=active]:bg-white"
                >
                  <Icon className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">{section.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Demographics Section */}
          <TabsContent value="demographics">
            <Card>
              <CardHeader>
                <CardTitle>Client Demographics</CardTitle>
                <CardDescription>
                  Basic identifying information for the client
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={assessment.demographics?.first_name || ""}
                      onChange={(e) => handleFieldChange("demographics", "first_name", e.target.value)}
                      data-testid="demographics-first-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={assessment.demographics?.last_name || ""}
                      onChange={(e) => handleFieldChange("demographics", "last_name", e.target.value)}
                      data-testid="demographics-last-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="preferred_name">Preferred Name</Label>
                    <Input
                      id="preferred_name"
                      value={assessment.demographics?.preferred_name || ""}
                      onChange={(e) => handleFieldChange("demographics", "preferred_name", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={assessment.demographics?.date_of_birth || ""}
                      onChange={(e) => handleFieldChange("demographics", "date_of_birth", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Gender</Label>
                    <Select
                      value={assessment.demographics?.gender || ""}
                      onValueChange={(value) => handleFieldChange("demographics", "gender", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="non_binary">Non-binary</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="funding_source">Funding Source</Label>
                    <Select
                      value={assessment.demographics?.funding_source || ""}
                      onValueChange={(value) => handleFieldChange("demographics", "funding_source", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select funding source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="VA">VA</SelectItem>
                        <SelectItem value="TriWest">TriWest</SelectItem>
                        <SelectItem value="Medicaid">Medicaid</SelectItem>
                        <SelectItem value="Private Pay">Private Pay</SelectItem>
                        <SelectItem value="Long Term Care Insurance">LTC Insurance</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="primary_phone">Primary Phone</Label>
                    <Input
                      id="primary_phone"
                      type="tel"
                      value={assessment.demographics?.primary_phone || ""}
                      onChange={(e) => handleFieldChange("demographics", "primary_phone", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={assessment.demographics?.email || ""}
                      onChange={(e) => handleFieldChange("demographics", "email", e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address_line1">Address</Label>
                  <Input
                    id="address_line1"
                    value={assessment.demographics?.address_line1 || ""}
                    onChange={(e) => handleFieldChange("demographics", "address_line1", e.target.value)}
                    placeholder="Street address"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={assessment.demographics?.city || ""}
                      onChange={(e) => handleFieldChange("demographics", "city", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={assessment.demographics?.state || ""}
                      onChange={(e) => handleFieldChange("demographics", "state", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input
                      id="zip_code"
                      value={assessment.demographics?.zip_code || ""}
                      onChange={(e) => handleFieldChange("demographics", "zip_code", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="primary_language">Primary Language</Label>
                    <Input
                      id="primary_language"
                      value={assessment.demographics?.primary_language || "English"}
                      onChange={(e) => handleFieldChange("demographics", "primary_language", e.target.value)}
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-6">
                    <Switch
                      id="interpreter_needed"
                      checked={assessment.demographics?.interpreter_needed || false}
                      onCheckedChange={(checked) => handleFieldChange("demographics", "interpreter_needed", checked)}
                    />
                    <Label htmlFor="interpreter_needed">Interpreter Needed</Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Emergency Contacts Section */}
          <TabsContent value="emergency_contacts">
            <Card>
              <CardHeader>
                <CardTitle>Emergency Contacts</CardTitle>
                <CardDescription>
                  Important contacts for emergencies and care coordination
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-500 text-sm">
                  Emergency contact management will be implemented in the next iteration.
                  For now, continue with other sections.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Medical History Section */}
          <TabsContent value="medical">
            <Card>
              <CardHeader>
                <CardTitle>Medical History</CardTitle>
                <CardDescription>
                  Diagnoses, medications, and allergies
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-500 text-sm">
                  Detailed medical history will be implemented in the next iteration.
                  For now, continue with other sections.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ADL Section */}
          <TabsContent value="adl">
            <Card>
              <CardHeader>
                <CardTitle>Activities of Daily Living (ADLs)</CardTitle>
                <CardDescription>
                  Assessment of basic self-care abilities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Bathing */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base font-medium">Bathing</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="bathing_independent"
                        checked={assessment.adl?.bathing_independent ?? true}
                        onCheckedChange={(checked) => handleFieldChange("adl", "bathing_independent", checked)}
                      />
                      <Label htmlFor="bathing_independent">Independent</Label>
                    </div>
                  </div>
                  {!assessment.adl?.bathing_independent && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label>Assistance Level</Label>
                        <Select
                          value={assessment.adl?.bathing_assistance_level || ""}
                          onValueChange={(value) => handleFieldChange("adl", "bathing_assistance_level", value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Stand-by">Stand-by (supervision)</SelectItem>
                            <SelectItem value="Hands-on">Hands-on (partial assist)</SelectItem>
                            <SelectItem value="Total">Total (complete assist)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Bathing Type</Label>
                        <Select
                          value={assessment.adl?.bathing_assistance_type || ""}
                          onValueChange={(value) => handleFieldChange("adl", "bathing_assistance_type", value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Shower">Shower</SelectItem>
                            <SelectItem value="Tub bath">Tub bath</SelectItem>
                            <SelectItem value="Sponge bath">Sponge bath</SelectItem>
                            <SelectItem value="Bed bath">Bed bath</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Dressing */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base font-medium">Dressing</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="dressing_independent"
                        checked={assessment.adl?.dressing_independent ?? true}
                        onCheckedChange={(checked) => handleFieldChange("adl", "dressing_independent", checked)}
                      />
                      <Label htmlFor="dressing_independent">Independent</Label>
                    </div>
                  </div>
                  {!assessment.adl?.dressing_independent && (
                    <div>
                      <Label>Assistance Level</Label>
                      <Select
                        value={assessment.adl?.dressing_assistance_level || ""}
                        onValueChange={(value) => handleFieldChange("adl", "dressing_assistance_level", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Stand-by">Stand-by (supervision)</SelectItem>
                          <SelectItem value="Hands-on">Hands-on (partial assist)</SelectItem>
                          <SelectItem value="Total">Total (complete assist)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Toileting */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base font-medium">Toileting</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="toileting_independent"
                        checked={assessment.adl?.toileting_independent ?? true}
                        onCheckedChange={(checked) => handleFieldChange("adl", "toileting_independent", checked)}
                      />
                      <Label htmlFor="toileting_independent">Independent</Label>
                    </div>
                  </div>
                  {!assessment.adl?.toileting_independent && (
                    <div className="space-y-4">
                      <div>
                        <Label>Assistance Level</Label>
                        <Select
                          value={assessment.adl?.toileting_assistance_level || ""}
                          onValueChange={(value) => handleFieldChange("adl", "toileting_assistance_level", value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Stand-by">Stand-by (supervision)</SelectItem>
                            <SelectItem value="Hands-on">Hands-on (partial assist)</SelectItem>
                            <SelectItem value="Total">Total (complete assist)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 mt-4">
                    <Switch
                      id="toileting_incontinence"
                      checked={assessment.adl?.toileting_incontinence || false}
                      onCheckedChange={(checked) => handleFieldChange("adl", "toileting_incontinence", checked)}
                    />
                    <Label htmlFor="toileting_incontinence">Incontinence</Label>
                  </div>
                  {assessment.adl?.toileting_incontinence && (
                    <div className="mt-4">
                      <Label>Incontinence Type</Label>
                      <Select
                        value={assessment.adl?.toileting_incontinence_type || ""}
                        onValueChange={(value) => handleFieldChange("adl", "toileting_incontinence_type", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Bladder">Bladder only</SelectItem>
                          <SelectItem value="Bowel">Bowel only</SelectItem>
                          <SelectItem value="Both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Transferring */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base font-medium">Transferring</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="transferring_independent"
                        checked={assessment.adl?.transferring_independent ?? true}
                        onCheckedChange={(checked) => handleFieldChange("adl", "transferring_independent", checked)}
                      />
                      <Label htmlFor="transferring_independent">Independent</Label>
                    </div>
                  </div>
                  {!assessment.adl?.transferring_independent && (
                    <div>
                      <Label>Assistance Level</Label>
                      <Select
                        value={assessment.adl?.transferring_assistance_level || ""}
                        onValueChange={(value) => handleFieldChange("adl", "transferring_assistance_level", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Stand-by">Stand-by (supervision)</SelectItem>
                          <SelectItem value="One person assist">One person assist</SelectItem>
                          <SelectItem value="Two person assist">Two person assist</SelectItem>
                          <SelectItem value="Mechanical lift">Mechanical lift required</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Eating */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base font-medium">Eating</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="eating_independent"
                        checked={assessment.adl?.eating_independent ?? true}
                        onCheckedChange={(checked) => handleFieldChange("adl", "eating_independent", checked)}
                      />
                      <Label htmlFor="eating_independent">Independent</Label>
                    </div>
                  </div>
                  {!assessment.adl?.eating_independent && (
                    <div>
                      <Label>Assistance Level</Label>
                      <Select
                        value={assessment.adl?.eating_assistance_level || ""}
                        onValueChange={(value) => handleFieldChange("adl", "eating_assistance_level", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Setup only">Setup only</SelectItem>
                          <SelectItem value="Cueing/prompting">Cueing/prompting</SelectItem>
                          <SelectItem value="Hand feeding">Hand feeding</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* IADL Section */}
          <TabsContent value="iadl">
            <Card>
              <CardHeader>
                <CardTitle>Instrumental Activities of Daily Living (IADLs)</CardTitle>
                <CardDescription>
                  Assessment of complex daily activities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Meal Preparation */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base font-medium">Meal Preparation</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="meal_prep_independent"
                        checked={assessment.iadl?.meal_prep_independent ?? true}
                        onCheckedChange={(checked) => handleFieldChange("iadl", "meal_prep_independent", checked)}
                      />
                      <Label htmlFor="meal_prep_independent">Independent</Label>
                    </div>
                  </div>
                  {!assessment.iadl?.meal_prep_independent && (
                    <div>
                      <Label>Frequency Needed</Label>
                      <Select
                        value={assessment.iadl?.meal_prep_frequency || ""}
                        onValueChange={(value) => handleFieldChange("iadl", "meal_prep_frequency", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Daily">Daily</SelectItem>
                          <SelectItem value="Multiple times per day">Multiple times per day</SelectItem>
                          <SelectItem value="Several times per week">Several times per week</SelectItem>
                          <SelectItem value="Weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Housekeeping */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base font-medium">Housekeeping</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="housekeeping_independent"
                        checked={assessment.iadl?.housekeeping_independent ?? true}
                        onCheckedChange={(checked) => handleFieldChange("iadl", "housekeeping_independent", checked)}
                      />
                      <Label htmlFor="housekeeping_independent">Independent</Label>
                    </div>
                  </div>
                  {!assessment.iadl?.housekeeping_independent && (
                    <div>
                      <Label>Tasks Needed (select all that apply)</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {["Vacuuming", "Dusting", "Dishes", "Mopping", "Bathroom cleaning", "Kitchen cleaning", "Laundry", "Bed making"].map(task => (
                          <div key={task} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`housekeeping_${task}`}
                              checked={(assessment.iadl?.housekeeping_tasks_needed || []).includes(task)}
                              onChange={(e) => {
                                const current = assessment.iadl?.housekeeping_tasks_needed || [];
                                const updated = e.target.checked
                                  ? [...current, task]
                                  : current.filter(t => t !== task);
                                handleFieldChange("iadl", "housekeeping_tasks_needed", updated);
                              }}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <Label htmlFor={`housekeeping_${task}`} className="text-sm">{task}</Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Medication Management */}
                <div className="border rounded-lg p-4">
                  <Label className="text-base font-medium">Medication Management</Label>
                  <div className="space-y-3 mt-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="medication_reminder_needed"
                        checked={assessment.iadl?.medication_reminder_needed || false}
                        onCheckedChange={(checked) => handleFieldChange("iadl", "medication_reminder_needed", checked)}
                      />
                      <Label htmlFor="medication_reminder_needed">Needs Medication Reminders</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="medication_setup_needed"
                        checked={assessment.iadl?.medication_setup_needed || false}
                        onCheckedChange={(checked) => handleFieldChange("iadl", "medication_setup_needed", checked)}
                      />
                      <Label htmlFor="medication_setup_needed">Needs Medication Setup/Organization</Label>
                    </div>
                  </div>
                </div>

                {/* Transportation */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base font-medium">Transportation</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="transportation_independent"
                        checked={assessment.iadl?.transportation_independent ?? true}
                        onCheckedChange={(checked) => handleFieldChange("iadl", "transportation_independent", checked)}
                      />
                      <Label htmlFor="transportation_independent">Independent</Label>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="transportation_drives"
                      checked={assessment.iadl?.transportation_drives || false}
                      onCheckedChange={(checked) => handleFieldChange("iadl", "transportation_drives", checked)}
                    />
                    <Label htmlFor="transportation_drives">Client Drives</Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cognitive/Behavioral Section */}
          <TabsContent value="cognitive">
            <Card>
              <CardHeader>
                <CardTitle>Cognitive & Behavioral Status</CardTitle>
                <CardDescription>
                  Assessment of cognitive function and behavioral concerns
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Orientation */}
                <div className="border rounded-lg p-4">
                  <Label className="text-base font-medium">Orientation</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="orientation_person"
                        checked={assessment.cognitive?.orientation_person ?? true}
                        onCheckedChange={(checked) => handleFieldChange("cognitive", "orientation_person", checked)}
                      />
                      <Label htmlFor="orientation_person">Person</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="orientation_place"
                        checked={assessment.cognitive?.orientation_place ?? true}
                        onCheckedChange={(checked) => handleFieldChange("cognitive", "orientation_place", checked)}
                      />
                      <Label htmlFor="orientation_place">Place</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="orientation_time"
                        checked={assessment.cognitive?.orientation_time ?? true}
                        onCheckedChange={(checked) => handleFieldChange("cognitive", "orientation_time", checked)}
                      />
                      <Label htmlFor="orientation_time">Time</Label>
                    </div>
                  </div>
                </div>

                {/* Memory */}
                <div className="border rounded-lg p-4">
                  <Label className="text-base font-medium">Memory</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Short-term Memory</Label>
                      <Select
                        value={assessment.cognitive?.memory_short_term || ""}
                        onValueChange={(value) => handleFieldChange("cognitive", "memory_short_term", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Intact">Intact</SelectItem>
                          <SelectItem value="Mildly impaired">Mildly impaired</SelectItem>
                          <SelectItem value="Moderately impaired">Moderately impaired</SelectItem>
                          <SelectItem value="Severely impaired">Severely impaired</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Long-term Memory</Label>
                      <Select
                        value={assessment.cognitive?.memory_long_term || ""}
                        onValueChange={(value) => handleFieldChange("cognitive", "memory_long_term", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Intact">Intact</SelectItem>
                          <SelectItem value="Mildly impaired">Mildly impaired</SelectItem>
                          <SelectItem value="Moderately impaired">Moderately impaired</SelectItem>
                          <SelectItem value="Severely impaired">Severely impaired</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Dementia */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <Switch
                      id="dementia_diagnosis"
                      checked={assessment.cognitive?.dementia_diagnosis || false}
                      onCheckedChange={(checked) => handleFieldChange("cognitive", "dementia_diagnosis", checked)}
                    />
                    <Label htmlFor="dementia_diagnosis" className="text-base font-medium">
                      Dementia Diagnosis
                    </Label>
                  </div>
                  {assessment.cognitive?.dementia_diagnosis && (
                    <div>
                      <Label>Type</Label>
                      <Select
                        value={assessment.cognitive?.dementia_type || ""}
                        onValueChange={(value) => handleFieldChange("cognitive", "dementia_type", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Alzheimers">Alzheimer&apos;s</SelectItem>
                          <SelectItem value="Vascular">Vascular</SelectItem>
                          <SelectItem value="Lewy Body">Lewy Body</SelectItem>
                          <SelectItem value="Frontotemporal">Frontotemporal</SelectItem>
                          <SelectItem value="Mixed">Mixed</SelectItem>
                          <SelectItem value="Unspecified">Unspecified</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Behavioral Concerns */}
                <div className="border rounded-lg p-4">
                  <Label className="text-base font-medium">Behavioral Concerns</Label>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {[
                      { key: "wandering_risk", label: "Wandering Risk" },
                      { key: "exit_seeking", label: "Exit Seeking" },
                      { key: "verbal_aggression", label: "Verbal Aggression" },
                      { key: "physical_aggression", label: "Physical Aggression" },
                      { key: "resistance_to_care", label: "Resistance to Care" },
                      { key: "anxiety", label: "Anxiety" },
                      { key: "depression", label: "Depression" },
                    ].map(item => (
                      <div key={item.key} className="flex items-center space-x-2">
                        <Switch
                          id={item.key}
                          checked={assessment.behavioral?.[item.key] || false}
                          onCheckedChange={(checked) => handleFieldChange("behavioral", item.key, checked)}
                        />
                        <Label htmlFor={item.key}>{item.label}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Safety & Mobility Section */}
          <TabsContent value="safety">
            <Card>
              <CardHeader>
                <CardTitle>Safety & Mobility</CardTitle>
                <CardDescription>
                  Home safety assessment and mobility evaluation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Fall Risk */}
                <div className="border rounded-lg p-4">
                  <Label className="text-base font-medium">Fall Risk Assessment</Label>
                  <div className="space-y-4 mt-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="history_of_falls"
                        checked={assessment.fall_risk?.history_of_falls || false}
                        onCheckedChange={(checked) => handleFieldChange("fall_risk", "history_of_falls", checked)}
                      />
                      <Label htmlFor="history_of_falls">History of Falls</Label>
                    </div>
                    {assessment.fall_risk?.history_of_falls && (
                      <div>
                        <Label>Falls in Last 6 Months</Label>
                        <Input
                          type="number"
                          min="0"
                          value={assessment.fall_risk?.falls_last_6_months || 0}
                          onChange={(e) => handleFieldChange("fall_risk", "falls_last_6_months", parseInt(e.target.value) || 0)}
                          className="w-32"
                        />
                      </div>
                    )}
                    <div>
                      <Label>Overall Fall Risk Level</Label>
                      <Select
                        value={assessment.fall_risk?.fall_risk_level || ""}
                        onValueChange={(value) => handleFieldChange("fall_risk", "fall_risk_level", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Low">Low</SelectItem>
                          <SelectItem value="Moderate">Moderate</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Mobility */}
                <div className="border rounded-lg p-4">
                  <Label className="text-base font-medium">Mobility Status</Label>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Ambulation Status</Label>
                      <Select
                        value={assessment.mobility?.ambulation_status || ""}
                        onValueChange={(value) => handleFieldChange("mobility", "ambulation_status", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Independent">Independent</SelectItem>
                          <SelectItem value="Device">Uses device independently</SelectItem>
                          <SelectItem value="Wheelchair">Wheelchair</SelectItem>
                          <SelectItem value="Bedbound">Bedbound</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {assessment.mobility?.ambulation_status === "Device" && (
                      <div>
                        <Label>Mobility Device</Label>
                        <Select
                          value={assessment.mobility?.ambulation_device || ""}
                          onValueChange={(value) => handleFieldChange("mobility", "ambulation_device", value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select device" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Cane">Cane</SelectItem>
                            <SelectItem value="Walker">Walker</SelectItem>
                            <SelectItem value="Rollator">Rollator</SelectItem>
                            <SelectItem value="Manual wheelchair">Manual wheelchair</SelectItem>
                            <SelectItem value="Electric wheelchair">Electric wheelchair</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Home Safety */}
                <div className="border rounded-lg p-4">
                  <Label className="text-base font-medium">Home Safety</Label>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Home Type</Label>
                      <Select
                        value={assessment.home_safety?.home_type || ""}
                        onValueChange={(value) => handleFieldChange("home_safety", "home_type", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="House">House</SelectItem>
                          <SelectItem value="Apartment">Apartment</SelectItem>
                          <SelectItem value="Condo">Condo</SelectItem>
                          <SelectItem value="Mobile home">Mobile home</SelectItem>
                          <SelectItem value="Assisted living">Assisted living facility</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="smoke_detectors"
                          checked={assessment.home_safety?.smoke_detectors ?? true}
                          onCheckedChange={(checked) => handleFieldChange("home_safety", "smoke_detectors", checked)}
                        />
                        <Label htmlFor="smoke_detectors">Smoke Detectors</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="handrails_present"
                          checked={assessment.home_safety?.handrails_present || false}
                          onCheckedChange={(checked) => handleFieldChange("home_safety", "handrails_present", checked)}
                        />
                        <Label htmlFor="handrails_present">Handrails Present</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="pets_in_home"
                          checked={assessment.home_safety?.pets_in_home || false}
                          onCheckedChange={(checked) => handleFieldChange("home_safety", "pets_in_home", checked)}
                        />
                        <Label htmlFor="pets_in_home">Pets in Home</Label>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduling Section */}
          <TabsContent value="scheduling">
            <Card>
              <CardHeader>
                <CardTitle>Scheduling Preferences</CardTitle>
                <CardDescription>
                  Client scheduling preferences and caregiver requirements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Recommended Hours per Week</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={assessment.scheduling?.recommended_hours_per_week || ""}
                      onChange={(e) => handleFieldChange("scheduling", "recommended_hours_per_week", parseFloat(e.target.value) || null)}
                      placeholder="e.g., 20"
                    />
                  </div>
                  <div>
                    <Label>Preferred Times</Label>
                    <Select
                      value={assessment.scheduling?.preferred_times || ""}
                      onValueChange={(value) => handleFieldChange("scheduling", "preferred_times", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select preferred time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Morning">Morning (6am-12pm)</SelectItem>
                        <SelectItem value="Afternoon">Afternoon (12pm-6pm)</SelectItem>
                        <SelectItem value="Evening">Evening (6pm-10pm)</SelectItem>
                        <SelectItem value="Flexible">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Preferred Days</Label>
                  <div className="grid grid-cols-4 md:grid-cols-7 gap-2 mt-2">
                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(day => (
                      <div key={day} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`day_${day}`}
                          checked={(assessment.scheduling?.preferred_days || []).includes(day)}
                          onChange={(e) => {
                            const current = assessment.scheduling?.preferred_days || [];
                            const updated = e.target.checked
                              ? [...current, day]
                              : current.filter(d => d !== day);
                            handleFieldChange("scheduling", "preferred_days", updated);
                          }}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <Label htmlFor={`day_${day}`} className="text-sm">{day.slice(0, 3)}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Caregiver Gender Preference</Label>
                    <Select
                      value={assessment.scheduling?.caregiver_gender_preference || ""}
                      onValueChange={(value) => handleFieldChange("scheduling", "caregiver_gender_preference", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="No preference" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No preference</SelectItem>
                        <SelectItem value="female">Female preferred</SelectItem>
                        <SelectItem value="male">Male preferred</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Language Requirement</Label>
                    <Input
                      value={assessment.scheduling?.caregiver_language_requirement || ""}
                      onChange={(e) => handleFieldChange("scheduling", "caregiver_language_requirement", e.target.value)}
                      placeholder="e.g., Spanish"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={goToPrevSection}
            disabled={activeSection === SECTIONS[0].key}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous Section
          </Button>
          <Button
            onClick={goToNextSection}
            disabled={activeSection === SECTIONS[SECTIONS.length - 1].key}
          >
            Next Section
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Complete Assessment Dialog */}
        <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark Assessment Complete?</AlertDialogTitle>
              <AlertDialogDescription>
                This will mark the assessment as complete and allow you to generate
                an AI-assisted care plan. You can still make changes later if needed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleMarkComplete}>
                Mark Complete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppShell>
  );
}
